import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';

import { AdminShell } from './layout/admin-shell';
import { keepListView } from './lib/use-list-view-state';
import { AdminAccountsPage } from './pages/admin-accounts-page';
import { AppVersionsPage } from './pages/app-versions-page';
import { AuditLogsPage } from './pages/audit-logs-page';
import { ChatRoomDetailPage } from './pages/chat-room-detail-page';
import { ChatRoomsPage } from './pages/chat-rooms-page';
import { CommunityPostDetailPage } from './pages/community-post-detail-page';
import { CommunityPostsPage } from './pages/community-posts-page';
import { CommunitySettingsPage } from './pages/community-settings-page';
import { CompanySettingsPage } from './pages/company-settings-page';
import { DashboardPage } from './pages/dashboard-page';
import { DepositReturnsPage } from './pages/deposit-returns-page';
import { GuidePage } from './pages/guide-page';
import { ListingSettingsPage } from './pages/listing-settings-page';
import { ListingsReviewPage } from './pages/listings-review-page';
import { MePage } from './pages/me-page';
import { OrderDetailPage } from './pages/order-detail-page';
import { OrdersPage } from './pages/orders-page';
import { PaymentSettingsPage } from './pages/payment-settings-page';
import { PaymentsPage } from './pages/payments-page';
import { PoliciesPage } from './pages/policies-page';
import { PolicyDetailPage } from './pages/policy-detail-page';
import { ProposalsPage } from './pages/proposals-page';
import { RefundsPage } from './pages/refunds-page';
import { ReportsPage } from './pages/reports-page';
import { RoyaltiesPage } from './pages/royalties-page';
import { SalesPage } from './pages/sales-page';
import { SchedulerJobsPage } from './pages/scheduler-jobs-page';
import { SecuritySettingsPage } from './pages/security-settings-page';
import { SettlementsPage } from './pages/settlements-page';
import { SiteSettingsPage } from './pages/site-settings-page';
import { SnsRequestsPage } from './pages/sns-requests-page';
import { SocialLinksSettingsPage } from './pages/social-links-settings-page';
import { SponsorshipApplicationsPage } from './pages/sponsorship-applications-page';
import { SponsorshipSettingsPage } from './pages/sponsorship-settings-page';
import { SponsorshipsReviewPage } from './pages/sponsorships-review-page';
import { SupportInquiriesPage } from './pages/support-inquiries-page';
import { SupportInquiryDetailPage } from './pages/support-inquiry-detail-page';
import { UserDetailPage } from './pages/user-detail-page';
import { UsersPage } from './pages/users-page';

const rootRoute = createRootRoute({ component: AdminShell });

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardPage,
});

const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders',
  component: OrdersPage,
  validateSearch: keepListView,
});

const orderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders/$orderId',
  component: OrderDetailPage,
});

const refundsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/refunds',
  component: RefundsPage,
  validateSearch: keepListView,
});

const paymentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payments',
  component: PaymentsPage,
  validateSearch: keepListView,
});

const depositReturnsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/deposit-returns',
  component: DepositReturnsPage,
  validateSearch: keepListView,
});

const proposalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/proposals',
  component: ProposalsPage,
  validateSearch: keepListView,
});

const salesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sales',
  component: SalesPage,
  validateSearch: keepListView,
});

const chatsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chats',
  component: ChatRoomsPage,
  validateSearch: keepListView,
});

const chatRoomDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chats/$roomId',
  component: ChatRoomDetailPage,
});

const listingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/listings',
  component: ListingsReviewPage,
  validateSearch: keepListView,
});

const adminAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admins',
  component: AdminAccountsPage,
});

const policiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/policies',
  component: PoliciesPage,
});

const policyDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/policies/$policyId',
  component: PolicyDetailPage,
});

const royaltiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/royalties',
  component: RoyaltiesPage,
  validateSearch: keepListView,
});

const settlementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settlements',
  component: SettlementsPage,
  validateSearch: keepListView,
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  component: ReportsPage,
  validateSearch: keepListView,
});

const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/support',
  component: SupportInquiriesPage,
  validateSearch: keepListView,
});

const supportDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/support/$inquiryId',
  component: SupportInquiryDetailPage,
});

const snsRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sns-requests',
  component: SnsRequestsPage,
  validateSearch: keepListView,
});

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  component: UsersPage,
  validateSearch: keepListView,
});

const userDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/$userId',
  component: UserDetailPage,
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    ...(typeof search.tab === 'string' ? { tab: search.tab } : {}),
  }),
});

/** 옛 북마크 호환 — /settings 는 결제 설정으로 */
const settingsRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  beforeLoad: () => {
    throw redirect({ to: '/settings/payment' });
  },
});

const paymentSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/payment',
  component: PaymentSettingsPage,
});

const siteSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/site',
  component: SiteSettingsPage,
});

const communitySettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/community',
  component: CommunitySettingsPage,
});

const sponsorshipsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sponsorships',
  component: SponsorshipsReviewPage,
  validateSearch: keepListView,
});

const sponsorshipApplicationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sponsorship-applications',
  component: SponsorshipApplicationsPage,
  validateSearch: keepListView,
});

const sponsorshipSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/sponsorship',
  component: SponsorshipSettingsPage,
});

const listingSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/listing',
  component: ListingSettingsPage,
});

const companySettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/company',
  component: CompanySettingsPage,
});

const socialLinksSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/social',
  component: SocialLinksSettingsPage,
});

const schedulerJobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/scheduler',
  component: SchedulerJobsPage,
});

const securitySettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/security',
  component: SecuritySettingsPage,
});

const appVersionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app-versions',
  component: AppVersionsPage,
});

const auditLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit-logs',
  component: AuditLogsPage,
  validateSearch: keepListView,
});

const mePageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/me',
  component: MePage,
});

const guideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guide',
  component: GuidePage,
});

const communityPostsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community',
  component: CommunityPostsPage,
  validateSearch: keepListView,
});

const communityPostDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community/$postId',
  component: CommunityPostDetailPage,
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  ordersRoute,
  orderDetailRoute,
  refundsRoute,
  paymentsRoute,
  depositReturnsRoute,
  proposalsRoute,
  salesRoute,
  chatsRoute,
  chatRoomDetailRoute,
  listingsRoute,
  settlementsRoute,
  royaltiesRoute,
  policiesRoute,
  policyDetailRoute,
  adminAccountsRoute,
  snsRequestsRoute,
  reportsRoute,
  supportRoute,
  supportDetailRoute,
  communityPostsRoute,
  communityPostDetailRoute,
  usersRoute,
  userDetailRoute,
  settingsRedirectRoute,
  paymentSettingsRoute,
  siteSettingsRoute,
  communitySettingsRoute,
  sponsorshipsRoute,
  sponsorshipApplicationsRoute,
  sponsorshipSettingsRoute,
  listingSettingsRoute,
  companySettingsRoute,
  socialLinksSettingsRoute,
  schedulerJobsRoute,
  securitySettingsRoute,
  appVersionsRoute,
  auditLogsRoute,
  mePageRoute,
  guideRoute,
]);

export const router = createRouter({
  routeTree,
  // 본문은 창이 아니라 #admin-content 가 스크롤한다 — 페이지를 옮기면 그 영역도 맨 위로
  scrollToTopSelectors: ['#admin-content'],
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
